/**
 * Email format validation regex.
 *
 * Matches `local@domain.tld` where the TLD is at least two **alphabetic**
 * characters. The alphabetic-TLD requirement mirrors the backend B2C validator
 * (`OnePlatform.EmailValidation`, jstedfast/EmailValidation with
 * `allowTopLevelDomains: false`), which rejects a final domain label that is
 * numeric — e.g. `aaa@aa.frt.123`. The previous `\.[^\s@]+$` tail accepted any
 * non-space TLD, so such an address passed the FE gate and only failed with a
 * raw 400 from OPHUB (OP-6095, reopened).
 *
 * Shape only: it says nothing about the TLD being *routable*. Prefer
 * {@link isValidEmail}, which adds the blocked-TLD layer — the regex stays
 * exported for the dedup search-readiness gates, which ask "plausible enough to
 * look up?" rather than "may this be saved?".
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
/**
 * Top-level domains the backend refuses as B2C destinations (OP-7457 / OP-7542).
 *
 * Mirrors `EmailValidationOptions.BlockedTlds` (the default list, which no service
 * overrides in appsettings) — IANA/RFC 6761 + 7686 special-use TLDs plus the
 * enterprise-intranet squats Eudonet policy rejects (ADR-0019). The CRM endpoints the
 * portal writes contacts, channels and users through all run `MustBeValidB2CEmail`, so
 * an address ending in one of these is a guaranteed 400: OPHUB channel create/update and
 * contact create, OPHUB household update, BFF/OPUS profile update, OPUS user invite,
 * OPINK send-mail / send-batch (From, To, Cc, Bcc, ReplyTo).
 *
 * NOT every email field in the app: the public donation form (`modules/collecte`) posts to
 * a backend with no B2C validator and keeps its own looser regex mirroring PHP `filter_var`
 * (see `useDonorValidation.ts`) — tightening it here would refuse donations the server
 * accepts. Gate a field with `isValidEmail` only when its endpoint runs the B2C validator.
 *
 * Keep in sync with `EmailValidationOptions.BlockedTlds`.
 */
export const BLOCKED_EMAIL_TLDS = new Set([
    // IANA / RFC 6761 + 7686 special-use TLDs — never publicly routable.
    "test",
    "example",
    "invalid",
    "localhost",
    "local",
    "onion",
    "internal",
    // Enterprise-intranet squats — not reserved by IANA but widely used on
    // private networks; Eudonet policy rejects them as B2C destinations.
    "corp",
    "lan",
    "localdomain",
]);
/**
 * The app-wide "may this address be saved?" gate — single source of truth for the
 * FE/BE email contract, so the two can't drift again.
 *
 * OP-7542: a blocked-domain address (`user@gmail.test`, `user@company.local`) is
 * well-formed, so the regex alone accepted it and the refusal only arrived from the
 * server — as a toast or a banner, away from the field. Folding the blocked-TLD layer
 * in here makes every consumer render its OWN existing `validation.emailInvalid`
 * message inline, below the email field, exactly as it already does for `test@test`.
 * The server-side errorCode mappings added by OP-7457 stay as the safety net for a
 * value this gate never saw (a read-only reference email, a concurrent edit).
 */
export function isValidEmail(value) {
    const trimmed = value?.trim() ?? "";
    if (!EMAIL_REGEX.test(trimmed))
        return false;
    // EMAIL_REGEX guarantees a dot before the alphabetic TLD, so the slice is never the
    // whole string. Lower-cased for the case-insensitive comparison the backend does with
    // its `StringComparer.OrdinalIgnoreCase` FrozenSet.
    const tld = trimmed.slice(trimmed.lastIndexOf(".") + 1).toLowerCase();
    return !BLOCKED_EMAIL_TLDS.has(tld);
}
